import React, { useCallback } from 'react';

import { Image, ImageBackground } from 'expo-image';
import { useExperimentGatedAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { menuSectionStyles } from 'features/menu/components/menu-section/styled';
import { TakeoverMenuSectionSubtitle } from 'features/menu/components/menu-section/takeover-menu-section-subtitle';
import {
  PriceAndCaloriesListWrapper,
  TileDescription,
  TileHeader,
  TileImage,
  TileImageWrapper,
} from 'features/menu/components/menu-tile-grid/styled';
import {
  IMenuTileProps,
  IPriceAndCaloriesProps,
} from 'features/menu/components/menu-tile-grid/types';
import { traceFirstMenuItemLoaded } from 'features/menu/utils/performance';
import { StyleSheet, View } from 'react-native';

import { Calories } from 'components/price-and-calories';
import { withTypography } from 'components/ui';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive, spacing } from 'styles/constants';

const ItemCalories = withTypography({
  numberOfLines: 1,
  color: getDesignToken('color-tertiary'),
  mt: '$1',
  mb: '$0',
  style: {
    textTransform: 'capitalize',
  },
});

const StyledItemPrice = withTypography({
  bold: true,
  color: getDesignToken('color-pricing'),
  mt: '$1',
  mr: '$1',
});

const priceAndCalories = ({
  calories,
  price,
  type,
  fontColorOverride,
}: IPriceAndCaloriesProps): JSX.Element | null => {
  const shouldRenderPriceAndCalories = price || calories;
  if (type === 'section' || !shouldRenderPriceAndCalories) {
    return null;
  }

  return (
    <PriceAndCaloriesListWrapper>
      <StyledItemPrice color={fontColorOverride}>{price}</StyledItemPrice>
      <Calories>
        <ItemCalories color={fontColorOverride}>{calories}</ItemCalories>
      </Calories>
    </PriceAndCaloriesListWrapper>
  );
};

// OLD legacy (not one menu)
/* NOTE: this component is used for main menu tiles and section tiles*/
const MenuTile = React.memo(
  ({
    image,
    objectFitContain,
    calories,
    name,
    type,
    price,
    id,
    accessibilityLabel = '',
    accessibilityLevel = 3,
    accessibilityRole = 'header',
  }: IMenuTileProps) => {
    const {
      menu: menuAppTakeover,
      pdp: pdpAppTakeover,
      fontFamily,
    } = useExperimentGatedAppTakeover();
    const takeoverSection = menuAppTakeover?.targetSectionIds?.[id];
    const takeoverItem = pdpAppTakeover?.targetMenuItemIds?.[id];
    const isTakeoverItem = !!takeoverItem;

    const fontColorOverride = takeoverSection?.fontColor || takeoverItem?.sectionFontColor;
    const priceAndCaloriesContent = priceAndCalories({ type, price, calories, fontColorOverride });

    const onImageDisplay = useCallback(() => {
      traceFirstMenuItemLoaded({ menuItemId: id, isOneMenu: 'false' });
    }, [id]);

    const Element =
      takeoverSection?.sectionTileBackgroundImage || takeoverItem?.sectionBackgroundImage
        ? ImageBackground
        : View;

    return (
      <>
        <Element
          source={
            takeoverSection?.sectionTileBackgroundImage || takeoverItem?.sectionBackgroundImage
          }
          style={[
            menuSectionStyles.menuSectionRow,
            {
              backgroundColor: isTakeoverItem
                ? takeoverSection?.itemBackgroundColor || takeoverItem?.sectionBackgroundColor
                : primitive.$white,
            },
          ]}
          accessibilityRole={accessibilityRole}
          accessibilityLevel={accessibilityLevel}
          accessibilityLabel={accessibilityLabel}
          testID="menu-tile-bk"
          dataSet={{ type, itemId: id }}
        >
          <TileImageWrapper>
            <TileImage
              image={image}
              alt={''}
              objectFitContain={objectFitContain}
              onDisplay={onImageDisplay}
              accessibilityHint={t('select')}
            />
          </TileImageWrapper>
          <TileDescription
            style={takeoverSection?.sectionTileSubtitle ? styles.contentWithSubtitle : undefined}
          >
            <TileHeader
              testID={`tile-header-${name?.toLowerCase().replace(/\s/g, '')}`}
              color={fontColorOverride}
              style={
                isTakeoverItem
                  ? [
                      fontFamily ? { fontFamily } : undefined,
                      takeoverSection?.titleMaxWidth
                        ? { maxWidth: takeoverSection.titleMaxWidth }
                        : undefined,
                    ]
                  : undefined
              }
            >
              {name}
            </TileHeader>
            <TakeoverMenuSectionSubtitle sectionId={id} />
            {priceAndCaloriesContent}
          </TileDescription>
          {takeoverSection?.sectionTileImage && (
            <Image
              pointerEvents="box-none"
              source={takeoverSection.sectionTileImage}
              contentFit="contain"
              style={[{ marginLeft: 'auto' }, takeoverSection.sectionTileImageStyle]}
            />
          )}
        </Element>
      </>
    );
  }
);

export default MenuTile;

const styles = StyleSheet.create({
  contentWithSubtitle: {
    flex: 1,
    gap: 4,
    minWidth: 0,
    paddingRight: spacing.$1,
  },
});
